import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStars } from '../context/StarContext';
import { calculateWeeksLoved } from '../utils/dateUtils';
import { createAndShareStarImage, shareContent } from '../utils/shareUtils';
import StillButton from './ui/StillButton';
import { motion } from 'framer-motion';
import { useOverlay } from '../App';
import ShareDialog from './ShareDialog';
import MetaTags from './MetaTags';

const ViewYourStarScreen = () => {
    const { starId } = useParams();
    const navigate = useNavigate();
    const { findStar, loading, error } = useStars();
    const { toggleOverlay } = useOverlay();
    const [star, setStar] = useState(null);
    const [useWebM, setUseWebM] = useState(true);
    const [isSafari, setIsSafari] = useState(false);
    // Fallback value in case starType is missing
    const [fallbackStarType, setFallbackStarType] = useState(1);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareImage, setShareImage] = useState(null);
    const [shareMetaImage, setShareMetaImage] = useState(null);
    const [shareData, setShareData] = useState(null);

    // Check for Safari and WebM support
    useEffect(() => {
        const checkBrowserSupport = () => {
            const ua = window.navigator.userAgent.toLowerCase();
            const isSafariCheck = (
                (ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1) || // Desktop Safari
                /iphone|ipod|ipad/.test(ua) // iOS Safari and all iOS WebViews
            );
            setIsSafari(isSafariCheck);

            // Only check WebM support if not Safari
            if (!isSafariCheck) {
                const video = document.createElement('video');
                const canPlayWebM = video.canPlayType('video/webm; codecs="vp8,vp9"');
                setUseWebM(canPlayWebM === 'probably' || canPlayWebM === 'maybe');
            } else {
                setUseWebM(false);
            }
        };

        checkBrowserSupport();
    }, []);

    // Hide the header when this component mounts
    useEffect(() => {
        toggleOverlay(false);
        
        // Clean up when unmounting
        return () => toggleOverlay(false);
    }, [toggleOverlay]);

    useEffect(() => {
        const loadStar = async () => {
            try {
                //console.log('Loading star with ID:', starId);
                const starData = await findStar(starId);
                //console.log('Loaded star data:', starData);
                setStar(starData);
                
                // Set a fallback star type if it's not defined
                if (!starData.starType) {
                    setFallbackStarType(Math.floor(Math.random() * 5) + 1);
                }
            } catch (err) {
                console.error('Error loading star:', err);
            }
        };

        loadStar();
    }, [starId, findStar]);

    const handleClose = () => {
        navigate('/space');
    };

    const handleShare = async () => {
        try {
            const { imageUrl, imageFile } = await createAndShareStarImage(star, weeksLoved, star.usid, starType || fallbackStarType);
            
            // Set the image URL for meta tags
            setShareMetaImage(imageUrl);
            
            // Store share data for native sharing
            const shareData = {
                title: `Still Loved`,
                text: `Today, I added a star to the Still Loved nebula in memory of, ${star.name}`,
                url: `${window.location.origin}/view-star/${star.usid}`,
                file: imageFile
            };

            // Always show the dialog and set share data
            setShareImage(imageUrl);
            setShareData(shareData);
            setShareDialogOpen(true);
        } catch (err) {
            console.error('Error sharing star:', err);
        }
    };

    const handleDownload = async () => {
        if (shareImage) {
            const a = document.createElement('a');
            a.href = shareImage;
            a.download = `${star.name}-star.png`;
            a.click();
        }
    };

    const calculateStarScale = (weeksLoved) => {
        // Default to minimum scale if no valid weeks
        if (!weeksLoved || weeksLoved < 0) return 0.5;
        
        // Cap weeks at 260 for maximum scale
        const cappedWeeks = Math.min(weeksLoved, 260);
        
        // Calculate scale using linear interpolation
        // 0 weeks = 0.5 scale
        // 260 weeks = 1.8 scale
        const scale = 0.5 + (cappedWeeks / 260) * (1.8 - 0.5);
        
        return scale;
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    if (!star) return null;

    const weeksLoved = calculateWeeksLoved(star.dob);
    const starScale = calculateStarScale(weeksLoved);
    // Use the star's starType or fallback to the random one if not available
    const starType = star.starType || fallbackStarType;

    const getVideoPath = () => {
        const starNumber = starType || fallbackStarType;
        
        if (isSafari) {
            // Use .mov for Safari (HEVC with alpha)
            return `/assets/stars/fallback/Star_${starNumber}.mov`;
        }
        
        // Use WebM for other browsers, fallback to MP4 if WebM not supported
        const format = useWebM ? 'webm' : 'fallback';
        const extension = useWebM ? 'webm' : 'mp4';
        return `/assets/stars/${format}/Star_${starNumber}.${extension}`;
    };

    return (
        <div id="star-container" className="fixed inset-0 flex flex-col overflow-y-auto">
            {/* Add MetaTags component */}
            {shareMetaImage && (
                <MetaTags
                    title={'Still Loved'}
                    description={`Today, I added a star to the Still Loved nebula in memory of, ${star.name}`}
                    imageUrl={shareMetaImage}
                    url={`${window.location.origin}/view-star/${star.usid}`}
                />
            )}
            
            <motion.div 
                className="min-h-screen w-full flex flex-col mb-40 lg:flex-row items-center mt-20 p-4 lg:mt-0 lg:p-8 gap-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"

            >
                {/* Star Video Section - Hidden on mobile */}
                <motion.div 
                    className="hidden lg:flex lg:flex-1 flex-col items-center justify-center pt-16 lg:pt-0"
                    variants={containerVariants}
                >
                    <div className="relative w-48 h-48 lg:w-auto lg:h-auto">
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{ 
                                transform: `scale(${starScale})`,
                                transition: 'transform 0.5s ease-out'
                            }}
                            disablePictureInPicture
                            className="max-w-full h-auto mix-blend-screen"
                            src={getVideoPath()}
                        />
                    </div>
                </motion.div>

                {/* Content Section */}
                <motion.div 
                    className="w-full lg:flex-1 flex flex-col justify-center px-4 lg:p-10 lg:mr-20 max-w-xl"
                    variants={containerVariants}
                >
                    <motion.div 
                        className="space-y-6"
                        variants={itemVariants}
                    >
                        <div>
                            <h1 className="text-still-white text-glow-still break-words text-4xl lg:text-5xl">
                                {star.name}
                            </h1>
                            
                            {star.message && (
                                <p className="text-still-white text-lg lg:text-xl mt-5 mb-10">
                                    {star.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            {/* Date Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <span className="text-still-white uppercase text-base w-1/3">Date:</span>
                                    <span className="text-cyan-300 text-lg uppercase w-2/3">
                                        {new Date(star.dob).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            {/* Place Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <span className="text-still-white uppercase text-base w-1/3">Place:</span>
                                    <span className="text-cyan-300 text-lg uppercase w-2/3">
                                        {star.pob}
                                    </span>
                                </div>
                            </div>

                            {/* USID Field */}
                            <div className="space-y-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-2"></div>
                                <div className="flex items-center">
                                    <span className="text-still-white uppercase text-base w-1/3">Star ID:</span>
                                    <span className="text-cyan-300 text-lg w-2/3">
                                        {star.usid}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Weeks still loved (desktop view) */}
                        <motion.div 
                            className="hidden lg:block"
                            variants={itemVariants}
                        >
                            <div className="h-px bg-gradient-to-r from-transparent via-still-blue to-transparent w-full mb-4"></div>
                            <p className="text-still-white text-glow-still text-2xl lg:text-3xl uppercase mt-10">
                                {weeksLoved} weeks still loved
                            </p>
                        </motion.div>

                        {/* Mobile Star Video */}
                        <motion.div 
                            className="lg:hidden flex flex-col items-center justify-center mt-8 pt-14"
                            variants={containerVariants}
                        >
                            <div 
                                className="relative w-48 h-48 flex items-center justify-center" 
                                style={{ 
                                    isolation: 'isolate'
                                }}
                            >
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        style={{ 
                                            transform: `scale(${starScale})`,
                                            transition: 'transform 0.5s ease-out'
                                        }}
                                        disablePictureInPicture
                                        className="max-w-full h-auto mix-blend-screen"
                                        src={getVideoPath()}
                                    />
                                </div>
                            </div>
                            <motion.p 
                                className="text-still-white text-glow-still mb-40 text-3xl lg:text-2xl uppercase mt-6 relative z-10"
                                variants={itemVariants}
                            >
                                {weeksLoved} weeks still loved
                            </motion.p>
                        </motion.div>

                        {/* Buttons - Desktop (under weeks still loved) */}
                        <motion.div 
                            className="pt-10 hidden lg:flex flex-row space-x-4 mb-10"
                            variants={itemVariants}
                        >
                            <StillButton
                                onClick={handleShare}
                                aria-label="Share Star"
                                className="w-auto"
                            >
                                SHARE
                            </StillButton>
                            <StillButton
                                onClick={handleClose}
                                aria-label="Close"
                                className="w-auto"
                            >
                                CLOSE
                            </StillButton>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </motion.div>

            {/* Mobile button underlay */}
            <motion.div 
                className={`fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-none mb-0 sm:mb-0 md:mb-0 lg:mb-0 z-50 pt-28 pb-20 py-4 bg-gradient-to-t from-black via-black to-transparent h-22`}
                variants={containerVariants}
            >
            
            </motion.div>
            
            {/* Buttons - Mobile (fixed at bottom) */}
            <motion.div 
                className="lg:hidden fixed bottom-0 left-0 flex flex-row justify-center w-full space-x-4 pointer-events-auto mb-20 sm:mb-16 md:mb-20 z-50 px-4"
                variants={itemVariants}
            >
                <StillButton
                    onClick={handleShare}
                    aria-label="Share Star"
                    className="w-auto"
                >
                    SHARE
                </StillButton>
                <StillButton
                    onClick={handleClose}
                    aria-label="Close"
                    className="w-auto"
                >
                    CLOSE
                </StillButton>
            </motion.div>

            

            {/* Share Dialog */}
            <ShareDialog
                isOpen={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                imageUrl={shareImage}
                starId={star?.usid}
                onDownload={handleDownload}
                shareData={shareData}
            />
        </div>
    );
};

export default ViewYourStarScreen; 